import { useState, useEffect } from 'react'
import { View, Text, TouchableOpacity, Modal, ScrollView, StyleSheet } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { supabase } from '../lib/supabase'
import { colors, fonts, radius, spacing } from '../lib/theme'
import GradientButton from './GradientButton'
import { useThemeColors } from '../lib/useThemeColors'
import { useAuth } from '../lib/AuthContext'

const typeConfig = {
  startlist:     { icon: 'clipboard-list-outline', lib: 'mci', color: '#3B82F6' },
  reminder:      { icon: 'clock-outline',          lib: 'mci', color: '#F59E0B' },
  results:       { icon: 'trophy-outline',         lib: 'mci', color: '#10B981' },
  league:        { icon: 'people-outline',         lib: 'ion', color: '#8B5CF6' },
  reminder_pick: { icon: 'clock-outline',          lib: 'mci', color: '#F59E0B' },
  team_invite:   { icon: 'handshake-outline',      lib: 'ion', color: colors.rosa },
  jersey:        { icon: 'tshirt-crew',            lib: 'mci', color: '#F59E0B' },
  rivalry:       { icon: 'sword-cross',            lib: 'mci', color: '#EF4444' },
  badge:         { icon: 'medal-outline',          lib: 'mci', color: '#FACC15' },
}

function NotifIcon({ cfg, size = 20 }) {
  if (cfg.lib === 'ion') return <Ionicons name={cfg.icon} size={size} color={cfg.color} />
  return <MaterialCommunityIcons name={cfg.icon} size={size} color={cfg.color} />
}

function formatDate(dateStr, t) {
  const d = new Date(dateStr)
  const diff = Math.floor((Date.now() - d) / 60000)
  if (diff < 1) return t('notifications.now')
  if (diff < 60) return t('notifications.minutes_ago', { count: diff })
  if (diff < 1440) return t('notifications.hours_ago', { count: Math.floor(diff / 60) })
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
}

// Priorità lingua utente: it → it, es → es, tutto il resto (en, fr, pt, null...) → en (fallback).
// Stessa logica usata server-side in send-notification / send-pick-reminders.
function resolveLang(userLanguage) {
  if (userLanguage === 'it') return 'it'
  if (userLanguage === 'es') return 'es'
  return 'en'
}

// Sceglie title/body nella lingua dell'utente tra le colonne restituite dalla RPC
// (title/body = IT, title_en/body_en, title_es/body_es). Se una traduzione manca
// (notifiche vecchie pre-i18n, o campi non popolati), ricade sull'italiano.
function localizeNotification(n, lang) {
  if (lang === 'en' && n.title_en) return { title: n.title_en, body: n.body_en }
  if (lang === 'es' && n.title_es) return { title: n.title_es, body: n.body_es }
  return { title: n.title, body: n.body }
}

export default function NotificheDrawer({ userId, userLanguage, onClose }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const styles = makeStyles(tc)
  const router = useRouter()
  const { switchLeague } = useAuth()
  const [notifiche, setNotifiche] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionMsg, setActionMsg] = useState({})
  const [markingAll, setMarkingAll] = useState(false)

  const lang = resolveLang(userLanguage)
  const unreadCount = notifiche.filter(n => !n.is_read).length

  const handleMarkAllRead = async () => {
    if (markingAll || unreadCount === 0) return
    setMarkingAll(true)
    try {
      await supabase.rpc('fn_mark_notifications_read', { p_id_user: userId })
      setNotifiche(prev => prev.map(n => ({ ...n, is_read: true })))
    } finally {
      setMarkingAll(false)
    }
  }

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.rpc('fn_get_notifications', { p_id_user: userId, p_limit: 20 })
      setNotifiche(data ?? [])
      setLoading(false)
    }
    load()
  }, [userId])

  const handleInviteAction = async (n, accept) => {
    setActionLoading(true)
    let id_team = null
    try { id_team = JSON.parse(n.body)?.id_team } catch {}
    if (!id_team) { setActionLoading(false); return }
    try {
      const fn = accept ? 'fn_accept_team_invite' : 'fn_refuse_team_invite'
      const { data } = await supabase.rpc(fn, { p_id_user: userId, p_id_team: id_team })
      const res = data?.[0]
      setActionMsg(prev => ({ ...prev, [n.id]: { ok: res?.success, text: res?.message } }))
    } catch (e) {
      setActionMsg(prev => ({ ...prev, [n.id]: { ok: false, text: e.message } }))
    } finally { setActionLoading(false) }
  }

  // richiesta di sfida: accetta o rifiuta direttamente dalla notifica
  const handleRivalryAction = async (n, accept) => {
    setActionLoading(true)
    try {
      const { data } = await supabase.rpc('fn_rivalry_respond', { p_id: n.meta?.rivalry_id, p_accept: accept })
      setActionMsg(prev => ({ ...prev, [n.id]: data?.ok
        ? { ok: true, text: t(accept ? 'rivalry.notif_accepted' : 'rivalry.notif_declined') }
        : { ok: false, text: t('rivalry.err_invalid') } }))
    } catch {
      setActionMsg(prev => ({ ...prev, [n.id]: { ok: false, text: t('rivalry.err_generic') } }))
    } finally { setActionLoading(false) }
  }

  const handleClick = (n) => {
    if (n.type === 'team_invite') return
    if (n.type === 'rivalry' && n.meta?.kind === 'request') return
    onClose()
    if (n.type === 'results') router.push('/leaderboard')
    else if (['startlist', 'reminder', 'reminder_pick'].includes(n.type)) {
      router.push(n.id_race ? `/pick?id_race=${n.id_race}` : '/pick')
    } else if (n.type === 'league') {
      router.push('/leagues')
    } else if (n.type === 'badge') {
      if (n.id_league) switchLeague(n.id_league)
      router.push('/Leagues/badges')
    } else if (n.type === 'rivalry') {
      if (n.id_league) switchLeague(n.id_league)
      router.push('/leagues')
    } else if (n.type === 'jersey') {
      // maglia conquistata: nella lega in cui l'hai vinta si vede sull'avatar della Home
      if (n.id_league) switchLeague(n.id_league)
      router.push('/')
    }
  }

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose} />
      <View style={styles.sheet}>
        <View style={styles.handleWrap}>
          <View style={styles.handle} />
        </View>
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Ionicons name="notifications-outline" size={16} color={tc.rosaText} />
            <Text style={styles.title}>{t('notifications.title')}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {unreadCount > 0 && (
              <TouchableOpacity
                style={styles.closeBtn}
                onPress={handleMarkAllRead}
                disabled={markingAll}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="checkmark-done-outline" size={16} color={markingAll ? tc.textMuted : tc.rosaText} />
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.closeBtn} onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={16} color={tc.textMuted} />
            </TouchableOpacity>
          </View>
        </View>
        <ScrollView style={styles.list}>
          {loading ? (
            <View style={styles.emptyWrap}>
              <Text style={styles.emptyText}>{t('common.loading')}</Text>
            </View>
          ) : notifiche.length === 0 ? (
            <View style={styles.emptyWrap}>
              <Ionicons name="notifications-outline" size={32} color={tc.textMuted} style={{ marginBottom: 8 }} />
              <Text style={styles.emptyText}>{t('notifications.empty')}</Text>
            </View>
          ) : (
            notifiche.map(n => {
              const cfg = typeConfig[n.type] ?? { icon: 'bullhorn-outline', lib: 'mci', color: tc.rosaText }
              const inviteAction = actionMsg[n.id]
              const { title: nTitle, body: nBody } = localizeNotification(n, lang)
              return (
                <TouchableOpacity
                  key={n.id}
                  onPress={() => handleClick(n)}
                  activeOpacity={n.type === 'team_invite' || (n.type === 'rivalry' && n.meta?.kind === 'request') ? 1 : 0.7}
                  style={[styles.notifRow, !n.is_read && styles.notifUnread]}
                >
                  <View style={[styles.notifIcon, { backgroundColor: cfg.color + '20', borderColor: cfg.color + '40' }]}>
                    <NotifIcon cfg={cfg} size={20} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={styles.notifTitleRow}>
                      <Text style={[styles.notifTitle, n.is_read && { fontWeight: '500' }]} numberOfLines={2}>
                        {nTitle}
                      </Text>
                      {!n.is_read && <View style={styles.unreadDot} />}
                    </View>
                    <Text style={styles.notifBody} numberOfLines={2}>{nBody}</Text>
                    <Text style={styles.notifDate}>{formatDate(n.created_at, t)}</Text>
                    {n.type === 'team_invite' && !inviteAction && (
                      <View style={styles.inviteActions}>
                        <GradientButton
                          onPress={() => handleInviteAction(n, true)}
                          disabled={actionLoading}
                          size="sm"
                          style={{ flex: 1 }}
                          icon={<Ionicons name="checkmark-circle-outline" size={13} color={tc.brandText} />}
                          iconPosition="left"
                        >
                          {t('notifications.accept')}
                        </GradientButton>
                        <TouchableOpacity
                          style={styles.refuseBtn}
                          onPress={() => handleInviteAction(n, false)}
                          disabled={actionLoading}
                        >
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                            <Ionicons name="close-circle-outline" size={13} color={tc.textMuted} />
                            <Text style={styles.refuseBtnText}>{t('notifications.refuse')}</Text>
                          </View>
                        </TouchableOpacity>
                      </View>
                    )}
                    {n.type === 'rivalry' && n.meta?.kind === 'request' && !inviteAction && (
                      <View style={styles.inviteActions}>
                        <GradientButton
                          onPress={() => handleRivalryAction(n, true)}
                          disabled={actionLoading}
                          size="sm"
                          style={{ flex: 1 }}
                          icon={<Ionicons name="checkmark-circle-outline" size={13} color={tc.brandText} />}
                          iconPosition="left"
                        >
                          {t('notifications.accept')}
                        </GradientButton>
                        <TouchableOpacity style={styles.refuseBtn} onPress={() => handleRivalryAction(n, false)} disabled={actionLoading} accessibilityRole="button">
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                            <Ionicons name="close-circle-outline" size={13} color={tc.textMuted} />
                            <Text style={styles.refuseBtnText}>{t('notifications.refuse')}</Text>
                          </View>
                        </TouchableOpacity>
                      </View>
                    )}
                    {inviteAction && (
                      <Text style={[styles.actionResult, { color: inviteAction.ok ? colors.success : tc.rosaText }]}>
                        {inviteAction.text}
                      </Text>
                    )}
                  </View>
                </TouchableOpacity>
              )
            })
          )}
        </ScrollView>
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  overlay:      { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)' },
  sheet:        { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: tc.bgCard, borderTopLeftRadius: 20, borderTopRightRadius: 20, borderWidth: 1, borderColor: tc.border, maxHeight: '75%' },
  handleWrap:   { alignItems: 'center', paddingVertical: 12 },
  handle:       { width: 36, height: 4, borderRadius: 2, backgroundColor: tc.border },
  header:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingTop: 8, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: tc.border },
  title:        { fontFamily: fonts.condensedBold, fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.5, color: tc.text },
  closeBtn:     { backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, borderRadius: radius.md, width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  list:         { flex: 1 },
  emptyWrap:    { padding: 32, alignItems: 'center' },
  emptyText:    { fontSize: 13, color: tc.textMuted, textAlign: 'center' },
  notifRow:     { flexDirection: 'row', gap: 12, padding: 14, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: tc.border },
  notifUnread:  { backgroundColor: tc.rosaSubtle },
  notifIcon:    { width: 36, height: 36, borderRadius: 10, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  notifTitleRow:{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 },
  notifTitle:   { fontFamily: fonts.condensedBold, fontSize: 13, color: tc.text, lineHeight: 18, flex: 1 },
  unreadDot:    { width: 8, height: 8, borderRadius: 4, backgroundColor: tc.rosa, flexShrink: 0, marginTop: 3 },
  notifBody:    { fontSize: 12, color: tc.textMuted, lineHeight: 17, marginBottom: 4 },
  notifDate:    { fontSize: 11, color: tc.textMuted, opacity: 0.7 },
  inviteActions:{ flexDirection: 'row', gap: 8, marginTop: 8 },
  refuseBtn:    { flex: 1, backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, borderRadius: radius.md, padding: 7, alignItems: 'center', justifyContent: 'center' },
  refuseBtnText:{ color: tc.textMuted, fontSize: 12, fontFamily: fonts.condensedBold },
  actionResult: { fontFamily: fonts.condensedBold, marginTop: 8, fontSize: 12 },
})